/**
 * Ordnung der Arbeitsschritte nach Hauptabschnitt — reine Logik, ohne
 * Datenbank und ohne Oberfläche.
 *
 * Grundsätze:
 *  - Die Liste gruppiert nach `hauptabschnitt` (leer = „Ohne Abschnitt“).
 *    Innerhalb eines Abschnitts zählt die Array-Reihenfolge, und die kommt
 *    beim Laden aus `order('sortierung')`. Die Sortierung ist NICHT eindeutig
 *    (Muster-Import, neue Schritte mit `schritte.length`) — Gleichstände
 *    kommen vor, ihre Reihenfolge legt die Datenbank fest.
 *  - Ein verschobener Schritt kommt ans ENDE des Ziels: er bekommt eine
 *    Sortierung hinter ALLEN Schritten des Plans (`naechsteSortierung`), nicht
 *    nur hinter denen des Ziels. Nur so steht er auch nach dem nächsten Laden
 *    noch hinten, egal wie die Gleichstände fallen.
 *  - Geändert werden nur `hauptabschnitt` und `sortierung` der verschobenen
 *    Schritte (`SchrittOrdnung`). Alle anderen Schritte behalten Sortierung
 *    und Platz — auch lokal (`wendeOrdnungAn`).
 *  - Abschnittsnamen werden getrimmt verglichen: „ Gerüst “ und „Gerüst“ sind
 *    derselbe Abschnitt.
 *
 * Geschrieben wird mit `schreibeOrdnung` (`schritteZusammenfuehren.ts`), die
 * Supabase-Anbindung liegt in `zusammenfuehrenDb.ts`.
 */
import { Arbeitsschritt } from './types';

/** Anzeigename der Gruppe ohne Abschnitt (wie in ArbeitsschritteListe). */
export const OHNE_ABSCHNITT = 'Ohne Abschnitt';

/** Abschnitt eines Schritts, getrimmt; '' = ohne Abschnitt. */
export const abschnittVon = (s: Pick<Arbeitsschritt, 'hauptabschnitt'>): string =>
  (s.hauptabschnitt || '').trim();

/**
 * Nächste freie Sortierung: max(sortierung)+1, leere Liste → 0. Auch für neue
 * Schritte. Unlesbare Werte (NULL aus Altbestand) zählen nicht mit.
 */
export const naechsteSortierung = (schritte: Pick<Arbeitsschritt, 'sortierung'>[]): number => {
  const max = schritte.reduce(
    (m, s) => (typeof s.sortierung === 'number' && Number.isFinite(s.sortierung) ? Math.max(m, s.sortierung) : m),
    -Infinity,
  );
  return max === -Infinity ? 0 : max + 1;
};

/** Neue Lage eines Schritts — genau die zwei Spalten, die geschrieben werden. */
export interface SchrittOrdnung { id: string; hauptabschnitt: string | null; sortierung: number }

/** Spaltenwert eines Abschnittsnamens: getrimmt, leer = NULL (wie `formToDb`). */
const spaltenWert = (abschnitt: string): string | null => abschnitt.trim() || null;

/**
 * Schritt `id` ans Ende von `ziel` ('' = ohne Abschnitt → hauptabschnitt null).
 * Gleicher Abschnitt (getrimmt verglichen), leeres/unbekanntes id → [].
 */
export const planeVerschieben = (schritte: Arbeitsschritt[], id: string, ziel: string): SchrittOrdnung[] => {
  if (!id) return [];
  const schritt = schritte.find(s => s.id === id);
  if (!schritt || abschnittVon(schritt) === ziel.trim()) return [];
  return [{ id, hauptabschnitt: spaltenWert(ziel), sortierung: naechsteSortierung(schritte) }];
};

/**
 * Alle Schritte aus `quelle` ans Ende von `ziel`, ihre Reihenfolge bleibt
 * (Array-Reihenfolge — das ist die angezeigte, nicht die gespeicherte
 * Sortierung). quelle === ziel oder Quelle leer → []. Sortierungen ab
 * naechsteSortierung fortlaufend.
 */
export const planeAbschnittZusammenfuehren = (schritte: Arbeitsschritt[], quelle: string, ziel: string): SchrittOrdnung[] => {
  const q = quelle.trim();
  if (q === ziel.trim()) return [];
  const verschoben = schritte.filter(s => abschnittVon(s) === q);
  if (!verschoben.length) return [];
  const start = naechsteSortierung(schritte);
  const hauptabschnitt = spaltenWert(ziel);
  return verschoben.map((s, i) => ({ id: s.id, hauptabschnitt, sortierung: start + i }));
};

/**
 * Lokal anwenden: geänderte Schritte bekommen NUR hauptabschnitt+sortierung
 * neu, werden aus ihrer Position entfernt und in Reihenfolge der neuen
 * sortierung ans Array-Ende gehängt; alle anderen bleiben unberührt an ihrem
 * Platz. Keine Änderungen → dieselbe Referenz.
 *
 * Ans Ende gehängt entspricht dem nächsten Laden: die neuen Sortierungen
 * liegen hinter allen anderen. Einträge für unbekannte ids zählen nicht.
 */
export const wendeOrdnungAn = (schritte: Arbeitsschritt[], ordnung: SchrittOrdnung[]): Arbeitsschritt[] => {
  const vorhanden = new Set(schritte.map(s => s.id));
  const nachId = new Map(ordnung.filter(o => vorhanden.has(o.id)).map(o => [o.id, o]));
  if (!nachId.size) return schritte;
  const bleiben = schritte.filter(s => !nachId.has(s.id));
  const verschoben = schritte
    .filter(s => nachId.has(s.id))
    .map(s => {
      const o = nachId.get(s.id)!;
      // Lokal wie gelesen: NULL steht im Typ als fehlender Abschnitt.
      return { ...s, hauptabschnitt: o.hauptabschnitt || undefined, sortierung: o.sortierung };
    })
    .sort((a, b) => a.sortierung - b.sortierung);
  return [...bleiben, ...verschoben];
};

// ─── Dubletten ─────────────────────────────────────────────────────────────

/** Anführungszeichen, die eine Bezeichnung umschließen können. */
const ANFUEHRUNG_ANFANG = /^["'„“”‚‘’«»‹›`]+/;
const ANFUEHRUNG_ENDE = /["'„“”‚‘’«»‹›`]+$/;

/**
 * Vergleichsschlüssel einer Bezeichnung: NFC, trim, Kleinschreibung,
 * Mehrfach-Leerraum → ein Leerzeichen, umschließende Anführungszeichen und
 * Satzzeichen . , ; : am Ende weg. Bewusst eng — lieber eine Dublette
 * übersehen als zwei verschiedene Arbeiten vorschlagen: Bindestriche,
 * Umlaute und Satzzeichen im Inneren bleiben, wie sie sind.
 */
export const bezeichnungsSchluessel = (bezeichnung: string): string => {
  let k = String(bezeichnung ?? '').normalize('NFC').replace(/\s+/g, ' ').trim().toLowerCase();
  // Abwechselnd, bis nichts mehr fällt: „„Gerüst“.“ und „"Gerüst."“ enden gleich.
  let vorher: string;
  do {
    vorher = k;
    k = k.replace(/[.,;:]+$/, '').replace(ANFUEHRUNG_ANFANG, '').replace(ANFUEHRUNG_ENDE, '').trim();
  } while (k !== vorher);
  return k;
};

/**
 * Gruppen gleich bezeichneter Schritte (≥ 2, auch über Abschnitte hinweg),
 * ids in Array-Reihenfolge, Gruppen nach erstem Auftreten; leere Bezeichnung
 * zählt nicht.
 */
export const findeDubletten = (schritte: Arbeitsschritt[]): string[][] => {
  const gruppen = new Map<string, string[]>();
  schritte.forEach(s => {
    const k = bezeichnungsSchluessel(s.bezeichnung);
    if (!k) return;
    const ids = gruppen.get(k);
    if (ids) ids.push(s.id);
    else gruppen.set(k, [s.id]);
  });
  return [...gruppen.values()].filter(ids => ids.length >= 2);
};
